/**
 * entityFactory.js
 * -----------------
 * Tiny factory/validator used by migrations and app code to create objects
 * that conform to a model template. Works in plain JS; no TypeScript required.
 *
 * Model template rules (simple + pragmatic):
 * - Template is a POJO where the value defines the *type* and the default.
 * - Supported type hints (by example):
 *     "" (string) | 0 (number) | true/false (boolean) | null | [] | {} | new Date(0)
 * - Nested objects/arrays are supported (arrays use the first element as item template).
 * - Use `options.required` to require specific keys; required can be ["id", ...] or a function.
 */

/**
 * Returns a coarse-grained type tag for a template value or runtime value.
 * @param {*} val - Any value.
 * @returns {"date"|"array"|"null"|"string"|"number"|"boolean"|"object"|"undefined"}
 */
function typeOfTemplate(val) {
  if (val instanceof Date) return "date";
  if (Array.isArray(val)) return "array";
  if (val === null) return "null";
  return typeof val; // "string" | "number" | "boolean" | "object" | "undefined"
}

/**
 * Deep-clone simple JSON-able structures plus Date.
 * Protects against reference sharing for arrays/objects in defaults.
 * @param {*} v - Value to clone.
 * @returns {*} - Deep copy.
 */
function deepClone(v) {
  if (v instanceof Date) return new Date(v.getTime());
  if (Array.isArray(v)) return v.map(deepClone);
  if (v && typeof v === "object") {
    const out = {};
    for (const k of Object.keys(v)) out[k] = deepClone(v[k]);
    return out;
  }
  return v;
}

/**
 * Coerces a single primitive to the type implied by the template value.
 * Complex types (object/array) are handled by callers.
 * @param {*} tmpl - Template default value establishing the target type.
 * @param {*} val - Input value to coerce; `null`/`undefined` produces template default.
 * @returns {*} - Coerced primitive (or default).
 */
function coercePrimitive(tmpl, val) {
  const t = typeOfTemplate(tmpl);
  if (val == null) return deepClone(tmpl);
  switch (t) {
    case "string":
      return String(val);
    case "number":
      return Number.isFinite(Number(val)) ? Number(val) : 0;
    case "boolean":
      return Boolean(val);
    case "date":
      return val instanceof Date ? new Date(val) : new Date(val);
    case "null":
      return null;
    default:
      return deepClone(val);
  }
}

/**
 * Recursively coerces `obj` into the shape/types defined by `modelTemplate`.
 *
 * - Objects: builds a new object with the template's keys coerced; by default keeps unknown keys.
 * - Arrays: uses the first element of the template array as the item template.
 * - Primitives/Date: coerces via `coercePrimitive`.
 *
 * @param {Object|Array|*} modelTemplate - Template structure (POJO / Array / primitive).
 * @param {*} obj - Source data to coerce.
 * @param {Object} [options]
 * @param {boolean} [options.dropUnknown=false] - If true, keys not in the template are dropped.
 * @returns {*} - New object/value matching the template's shape and types.
 */
export function coerceToModel(modelTemplate, obj, options = {}) {
  const { dropUnknown = false } = options;
  if (Array.isArray(modelTemplate)) {
    const itemTmpl = modelTemplate[0] ?? null;
    if (!itemTmpl) return Array.isArray(obj) ? obj.slice() : [];
    const src = Array.isArray(obj) ? obj : [];
    return src.map((it) => coerceToModel(itemTmpl, it, options));
  }
  const t = typeOfTemplate(modelTemplate);
  if (t !== "object") return coercePrimitive(modelTemplate, obj);

  const out = {};
  const keys = Object.keys(modelTemplate);
  for (const k of keys) {
    out[k] = coerceToModel(modelTemplate[k], obj ? obj[k] : undefined, options);
  }
  if (!dropUnknown && obj && typeof obj === "object") {
    for (const k of Object.keys(obj)) if (!(k in out)) out[k] = obj[k];
  }
  return out;
}

/**
 * Validates that `obj` matches the shape/types implied by `modelTemplate`.
 * - Arrays validate each element against the array's first-item template (if present).
 * - Objects validate known keys; when `strict=true`, unknown keys are flagged as errors.
 * - Numbers accept numeric strings (e.g., "42") as OK by default.
 *
 * @param {Object|Array|*} modelTemplate - Template describing shape and types.
 * @param {*} obj - Candidate object/value to validate.
 * @param {Object} [opts]
 * @param {boolean} [opts.strict=false] - If true, unknown keys are errors.
 * @returns {{ok:boolean, errors:string[]}}
 */
export function validateTypes(modelTemplate, obj, { strict = false } = {}) {
  const errors = [];
  function walk(tmpl, val, path) {
    if (Array.isArray(tmpl)) {
      if (!Array.isArray(val)) {
        errors.push(`${path} expected array`);
      } else if (tmpl[0]) {
        val.forEach((v, i) => walk(tmpl[0], v, `${path}[${i}]`));
      }
      return;
    }

    const t = typeOfTemplate(tmpl);
    if (t === "object") {
      if (!val || typeof val !== "object" || Array.isArray(val)) {
        errors.push(`${path} expected object`);
        return;
      }
      for (const k of Object.keys(tmpl)) walk(tmpl[k], val[k], `${path}.${k}`);
      if (strict) {
        for (const k of Object.keys(val))
          if (!(k in tmpl)) errors.push(`${path}.${k} is unknown`);
      }
      return;
    }

    const expected = typeOfTemplate(tmpl);
    const actual = typeOfTemplate(val);
    if (
      actual !== expected &&
      !(expected === "number" && actual === "string")
    ) {
      errors.push(`${path} expected ${expected}, got ${actual}`);
    }
  }
  walk(modelTemplate, obj, "$");
  return { ok: errors.length === 0, errors };
}

/**
 * Creates a new entity that conforms to the given model template.
 * - Applies shape/type coercion.
 * - Enforces required keys (throws if missing/empty).
 * - Optionally validates strictly (throws if mismatched).
 *
 * @param {Object|Array|*} modelTemplate - Template used to shape and type the entity.
 * @param {Object} [overrides={}] - Properties to set/override on the new entity.
 * @param {Object} [options]
 * @param {string[]|function(Object):string[]} [options.required=[]] - Required keys or a function returning a list.
 * @param {boolean} [options.dropUnknown=false] - Drop keys not in template.
 * @param {boolean} [options.strict=false] - If true, throw on validation errors (including unknown keys when strict).
 * @throws {Error & {code:string}} E_REQUIRED if a required key is missing; E_TYPES if strict type validation fails.
 * @returns {*} - Coerced, validated entity.
 */
export function createEntity(modelTemplate, overrides = {}, options = {}) {
  const { required = [], dropUnknown = false, strict = false } = options;

  const entity = coerceToModel(modelTemplate, overrides, { dropUnknown });

  const requiredList =
    typeof required === "function" ? required(modelTemplate) : required;
  for (const key of requiredList) {
    if (!(key in entity) || entity[key] == null || entity[key] === "") {
      const err = new Error(`Missing required key: ${key}`);
      err.code = "E_REQUIRED";
      throw err;
    }
  }

  const v = validateTypes(modelTemplate, entity, { strict });
  if (!v.ok && strict) {
    const err = new Error(
      `Type validation failed: \n- ${v.errors.join("\n- ")}`
    );
    err.code = "E_TYPES";
    throw err;
  }

  return entity;
}

// previous
// /**
//  * Ensure that an object contains all specified keys with non-empty values.
//  *
//  * Checks each key in `requiredKeys` against `obj` and throws an Error if the corresponding
//  * value is `undefined`, `null`, or an empty string (`""`). Numeric zero (0) and boolean
//  * false are considered valid values.
//  *
//  * @param {Object} [obj={}] - The object to validate.
//  * @param {string[]} [requiredKeys=[]] - Array of keys that must exist on `obj`.
//  * @throws {Error} If a required key is missing or its value is `undefined`, `null`, or `""`.
//  * @returns {boolean}
//  * @example
//  * // Valid: age is 0 (allowed), name is non-empty
//  * hasRequiredKeys({ name: 'Alice', age: 0 }, ['name', 'age']);
//  *
//  * // Throws: name is an empty string
//  * hasRequiredKeys({ name: '', age: 0 }, ['name']);
//  */
// export function hasRequiredKeys(obj = {}, requiredKeys = []) {
//   for (const key of requiredKeys) {
//     const v = obj[key];
//     if (v === undefined || v === null || v === "") {
//       throw new Error(`Missing required field: "${key}"`);
//     }
//   }
//   return true;
// }

// /**
//  * Validate that a value matches one of the expected type(s).
//  *
//  * Supported expected type strings:
//  * - "string"    : typeof value === "string"
//  * - "number"    : typeof value === "number" && Number.isFinite(value)
//  * - "boolean"   : typeof value === "boolean"
//  * - "array"     : Array.isArray(value)
//  * - "object"    : non-null plain object (typeof === "object" && !Array.isArray(value))
//  * - "null"      : value === null
//  * - "undefined" : value === undefined
//  * - "date"      : either a Date instance or a string parseable by Date.parse()
//  * - "uuid"      : string matching a v4 UUID pattern (case-insensitive)
//  *
//  * For any other string value passed as an expected type, the function falls back to
//  * a basic typeof comparison (for example: "function", "symbol", etc.).
//  *
//  * expected may be either a single string or an array of strings; the function
//  * returns true if the value satisfies at least one of the provided expected types.
//  *
//  * @param {*} value - The value to validate.
//  * @param {string|string[]} expected - A type name or an array of type names to validate against.
//  * @returns {boolean} True if the value matches one of the expected type(s), otherwise false.
//  *
//  * @example
//  * validateValueType("hello", "string"); // true
//  * validateValueType(123, ["string","number"]); // true
//  * validateValueType(null, "object"); // false (null is explicitly excluded from "object")
//  * validateValueType("2020-01-01", "date"); // true (parseable date string)
//  * validateValueType("550e8400-e29b-41d4-a716-446655440000", "uuid"); // true (v4 UUID)
//  */
// function validateValueType(value, expected) {
//   const expecteds = Array.isArray(expected) ? expected : [expected];

//   const isType = (val, t) => {
//     switch (t) {
//       case "string":
//         return typeof val === "string";
//       case "number":
//         return typeof val === "number" && Number.isFinite(val);
//       case "boolean":
//         return typeof val === "boolean";
//       case "array":
//         return Array.isArray(val);
//       case "object":
//         return val !== null && typeof val === "object" && !Array.isArray(val);
//       case "null":
//         return val === null;
//       case "undefined":
//         return val === undefined;
//       case "date":
//         return (
//           val instanceof Date ||
//           (typeof val === "string" && !Number.isNaN(Date.parse(val)))
//         );
//       case "uuid":
//         // [ ] need to add cuid
//         return (
//           typeof val === "string" &&
//           /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
//             val
//           )
//         );
//       default:
//         // fall back to typeof comparison for custom strings
//         return typeof val === t;
//     }
//   };

//   return expecteds.some((t) => isType(value, t));
// }

// /**
//  * Validates an object against a map of expected types.
//  * Only validates keys present in `obj` so you can safely pass partial overrides.
//  *
//  * @param {Object} obj - values to validate
//  * @param {Record<string, string|string[]>} typeMap - { key: "type" | ["typeA","typeB"] }
//  * @throws {TypeError}
//  */
// export function validateTypes(obj = {}, typeMap = {}) {
//   for (const [key, expected] of Object.entries(typeMap)) {
//     if (!(key in obj)) continue; // absent keys are fine for overrides
//     const value = obj[key];
//     if (!validateValueType(value, expected)) {
//       const exp = Array.isArray(expected) ? expected.join(" | ") : expected;
//       const actual =
//         value === null ? "null" : Array.isArray(value) ? "array" : typeof value;
//       throw new TypeError(
//         `Invalid type for "${key}": expected ${exp}, got ${actual}`
//       );
//     }
//   }
// }

// /**
//  * Generic entity factory:
//  * 1) validates required keys in overrides
//  * 2) validates types for overrides
//  * 3) merges base + overrides
//  * 4) optionally updates timestamps if present
//  *
//  * @param {Object} base - default/base object
//  * @param {Object} overrides - values to apply
//  * @param {string[]} requiredKeys - required keys for creation
//  * @param {Record<string, string|string[]>} typeMap - expected types for keys (validates only keys in overrides)
//  * @param {{touchUpdatedAt?: boolean}} options
//  * @returns {Object}
//  */
// export function createEntity(
//   base,
//   overrides = {},
//   requiredKeys = [],
//   typeMap = {},
//   options = {}
// ) {
//   hasRequiredKeys(overrides, requiredKeys);
//   validateTypes(overrides, typeMap);

//   const entity = { ...base, ...overrides };

//   // auto-timestamp if fields exist in base
//   const now = Date.now();

//   if (entity.created === undefined && base.created !== undefined) {
//     entity.created = now;
//   }
//   if (options.touchUpdated !== false && "updated" in entity) {
//     entity.updated = now;
//   }

//   return entity;
// }
